import { Conditions } from '@vsolv/packages/conditions/domain';

/**
 * Adds derived properties to a property-values bag before condition / pricing evaluation.
 *
 * Derives sibling `age` properties from submitted `year` properties.
 * Example: `roadVehicleYear` -> `roadVehicleAge`.
 *
 * Pure function — the original object is not mutated.
 * When the source value is missing the derived key is simply omitted,
 * so existing behaviour is preserved.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function normalizePropertyValues(propertyValues: Record<string, any>, asOfYear?: number): Record<string, any> {
  if (!propertyValues || typeof propertyValues !== 'object') return propertyValues;

  const currentYear = asOfYear ?? new Date().getFullYear();
  return normalizeObjectPropertyValues(propertyValues, currentYear);
}

function normalizeObjectPropertyValues(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  propertyValues: Record<string, any>,
  currentYear: number
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> {
  const nextValues = { ...propertyValues };

  for (const [key, value] of Object.entries(propertyValues)) {
    if (isPlainObject(value)) {
      nextValues[key] = normalizeObjectPropertyValues(value, currentYear);
    }

    if (!isYearLikeKey(key)) continue;

    const ageKey = toAgeKey(key);
    const year = toYearNumber(value);

    if (year === null) {
      delete nextValues[ageKey];
      continue;
    }

    nextValues[ageKey] = currentYear - year;
  }

  return nextValues;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
}

function isYearLikeKey(key: string): boolean {
  return key.toLowerCase().includes('year');
}

export function toAgeKey(yearKey: string): string {
  const ageKey = yearKey.replace(/year/i, match => {
    if (match === match.toUpperCase()) return 'AGE';
    if (match[0] === match[0].toUpperCase()) return 'Age';
    return 'age';
  });

  return ageKey === yearKey ? `${yearKey}Age` : ageKey;
}

function toYearNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  if (typeof value === 'string' && value.trim().length) {
    const year = Number(value);
    return Number.isFinite(year) ? year : null;
  }

  return null;
}

// Namespace for dimension values in the property bag — the picker stores
// `dimension.<id>` paths that resolve against it, kept apart from property valueKeys.
export const DIMENSION_VALUE_NAMESPACE = 'dimension';

// Folds dimension values into the property bag under `dimension.<id>` so they can be used
// as plan config values. Numeric strings become numbers; the input isn't mutated.
export function mergeDimensionValues(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  propertyValues: Record<string, any>,
  dimensionValues?: Record<string, unknown> | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> {
  if (!dimensionValues || typeof dimensionValues !== 'object') return propertyValues;

  const existing = propertyValues?.[DIMENSION_VALUE_NAMESPACE];
  const bag: Record<string, unknown> = existing && typeof existing === 'object' ? { ...existing } : {};

  for (const [dimensionId, raw] of Object.entries(dimensionValues)) {
    if (raw === undefined || raw === null) continue;

    const asNumber =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))
          ? Number(raw)
          : null;

    bag[dimensionId] = asNumber !== null ? asNumber : raw;
  }

  return { ...propertyValues, [DIMENSION_VALUE_NAMESPACE]: bag };
}

/** Converts a dollar-denominated property or dimension value to cents. */
export function propertyValueToCents(value: unknown): number {
  const numericValue =
    typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : Number.NaN;

  return Number.isFinite(numericValue) ? Math.round(numericValue * 100) : 0;
}

/** Normalizes a term-duration property/dimension value to a whole number of months, or null if not numeric. */
export function propertyValueToMonths(value: unknown): number | null {
  const numericValue =
    typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : Number.NaN;

  return Number.isFinite(numericValue) ? Math.round(numericValue) : null;
}

/**
 * Evaluates a monetary rule (liability limit, deductible, etc.) and returns cents.
 *
 * Static defaults and block values are stored in cents. Values resolved from a
 * `valuePropertyPath` (properties / dimensions) are in dollars and converted to cents,
 * matching plan-level liability limit evaluation.
 *
 * When a property path is missing, falls back to the configured cents value
 * (`block.value` or `rule.defaultValue`) so callers always receive a number.
 */
export function evaluateMonetaryRule(
  rule: Conditions.Rule<number>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  propertyValues: Record<string, any>
): number {
  for (const block of rule.blocks) {
    if (Conditions.evaluateConditionGroup(block.condition, propertyValues)) {
      if (block.valuePropertyPath) {
        const value = Conditions.getPropertyValue(block.valuePropertyPath, propertyValues);
        if (value === null || value === undefined) return toCentsFallback(block.value);
        return propertyValueToCents(value);
      }
      return toCentsFallback(block.value);
    }
  }

  if (rule.valuePropertyPath) {
    const value = Conditions.getPropertyValue(rule.valuePropertyPath, propertyValues);
    if (value === null || value === undefined) return toCentsFallback(rule.defaultValue);
    return propertyValueToCents(value);
  }

  return toCentsFallback(rule.defaultValue);
}

function toCentsFallback(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
